/**
 * Die Portal-Shell: Header, Hauptnavigation, Erscheinungsumschalter.
 *
 * Sie ist nach ADR-040 der **einzige** Ort, an dem Header und Navigation
 * gezeichnet werden — auch über eingebetteten Fremdoberflächen. Deshalb muss
 * ein Wechsel der Ansicht das Dokument bestehen lassen: Ein Seitenwechsel, der
 * neu lädt, würfe den Rahmen samt eingebettetem Dienst jedes Mal weg. Das ist
 * der Grund für clientseitiges Routing in ADR-043, nicht Geschmack.
 *
 * Die Navigationspunkte stehen in `07` §7.3. **Zwei davon sind bedingt**, und
 * das ist der Unterschied zwischen „Ansicht kommt später" und „gibt es hier
 * nicht": Die übrigen zehn stehen im Menü, auch wo die Ansicht dahinter noch
 * fehlt — ein Menü, das später wächst, verschiebt jede Gewöhnung. Der
 * Assistent und die Administration stehen dort **nicht** vorsorglich, weil
 * ihre Abwesenheit kein Zwischenstand ist, sondern eine Aussage: Der Kunde hat
 * die KI nicht gebucht, oder dieser Benutzer verwaltet den Betrieb nicht.
 *
 * Bis zum 05.09.2026 fehlten beide ganz, und der Kommentar hier behauptete
 * Vollständigkeit — zehn von zwölf Punkten, mit einer Zeile darüber, die das
 * Gegenteil sagte.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router";

import type { Merkmale } from "@hwc/config";
import type { Rolle } from "@hwc/contracts";
import { Dialog } from "@hwc/ui";

import { Fehlergrenze } from "./Fehlergrenze.js";
import {
  MARKE,
  seitentitel,
  sichtbareNavigation,
  sichtbareNeueintraege,
} from "./navigation.js";
import { WAHLEN, type Wahl } from "./erscheinung.js";

/** Beschriftung der Wahl. `automatisch` sagt, wonach es sich richtet. */
const WAHLTEXT: Record<Wahl, string> = {
  automatisch: "Wie das Gerät",
  hell: "Hell",
  dunkel: "Dunkel",
};

export interface ShellEigenschaften {
  /** Was eingestellt ist — nicht, was daraus folgt. */
  readonly wahl: Wahl;
  readonly beiWahl: (w: Wahl) => void;
  /** Die Merkmale des Kundenstacks aus der Laufzeitkonfiguration (ADR-044). */
  readonly merkmale: Partial<Merkmale>;
  /** Rollen des angemeldeten Benutzers; leer, solange keine Anmeldung steht. */
  readonly rollen: readonly Rolle[];
  /**
   * Was zwischen Navigation und Darstellungsumschalter in den Kopf gehoert.
   *
   * Ein Schlitz statt eines festen Bauteils, weil die Shell nicht wissen soll,
   * **wer** angemeldet ist. Sie zeichnet den Rahmen; wen sie darin anzeigt,
   * entscheidet die Anwendung. Ohne diese Trennung muesste sie die Sitzung
   * kennen — und damit auch, dass es im Demonstrationsstand eine Personenwahl
   * gibt und im Betrieb eine Anmeldung.
   */
  readonly kopfZusatz?: ReactNode;
  /**
   * Ein Band über jeder Ansicht — für Aussagen, die überall gelten.
   *
   * **Über dem Inhalt und nicht im Kopf.** Der Kopf trägt Navigation und
   * Personenwahl; was dort noch dazukommt, liest sich als Bedienelement. Ein
   * Satz, der die Ansicht einordnet, gehört über die Ansicht.
   *
   * **In der Shell und nicht in den Routen.** Wer über einen Link mitten in
   * eine Auftragsakte springt, sieht die Startseite nie — ein Hinweis, der
   * nur dort steht, erreicht ihn nicht.
   */
  readonly band?: ReactNode;
  /**
   * Was der „Neu"-Dialog anbietet, wenn er mehr kann als verweisen.
   *
   * Derselbe Gedanke wie bei `kopfZusatz`: Die Shell zeichnet den Rahmen und
   * kennt weder Bestand noch Sitzung. `07` §7.3 verlangt, dass der Dialog
   * **erstellt** — was, entscheidet die Anwendung. Ohne diesen Schlitz müsste
   * die Shell wissen, wie man einen Auftrag anlegt, und damit auch, dass es
   * Aufträge gibt.
   *
   * Ist er leer, bleibt es bei der Liste der Wege — der ehrliche
   * Zwischenstand für alles, was noch keinen Adapter hat.
   */
  readonly neuInhalt?: ReactNode;
}

export function Shell({
  wahl,
  beiWahl,
  merkmale,
  rollen,
  kopfZusatz,
  band,
  neuInhalt,
}: ShellEigenschaften) {
  const [menueOffen, setzeMenue] = useState(false);
  const menueId = useId();
  const knopf = useRef<HTMLButtonElement | null>(null);
  const inhalt = useRef<HTMLElement | null>(null);
  const vorigerPfad = useRef<string | null>(null);
  const ort = useLocation();

  const schliessen = useCallback(() => setzeMenue(false), []);

  const punkte = useMemo(
    () => sichtbareNavigation(merkmale, rollen),
    [merkmale, rollen],
  );

  const [neuOffen, setzeNeu] = useState(false);
  const neueintraege = useMemo(
    () => sichtbareNeueintraege(merkmale, rollen),
    [merkmale, rollen],
  );
  const neuSchliessen = useCallback(() => setzeNeu(false), []);

  // Ein Wechsel der Ansicht schließt das Menü. Ohne das bliebe es auf einem
  // schmalen Bildschirm über dem Inhalt stehen, den es gerade geöffnet hat.
  useEffect(() => {
    schliessen();
    // Der Dialog schlieszt mit. Er fuehrt in einen Bereich — bliebe er
    // darueber stehen, verdeckte er genau das, wohin er gerade gefuehrt hat.
    setzeNeu(false);
  }, [ort.pathname, schliessen]);

  /**
   * Was ein Seitenwechsel sonst von selbst täte — hier von Hand.
   *
   * Bei clientseitigem Routing (ADR-043) lädt der Browser nichts neu. Er setzt
   * deshalb **keinen Titel** und **verschiebt keinen Fokus**, und beides fehlt
   * an Stellen, die niemandem sofort auffallen:
   *
   *   Titel   Verlauf, Lesezeichen und Tab-Leiste zeigten für alle zwölf
   *           Ansichten denselben Text.
   *   Fokus   blieb auf dem angeklickten Menüpunkt stehen. Wer mit der
   *           Tastatur weiterarbeitet, tabbt dann durch die restliche
   *           Navigation statt durch den neuen Inhalt, und ein
   *           Vorlesewerkzeug sagt gar nichts — für den Bildschirm hat sich
   *           alles geändert, für den Fokus nichts.
   *
   * **Nicht beim ersten Rendern.** Ein Fokussprung beim Laden nähme dem
   * Sprunglink seinen Sinn: Er ist der erste Tabulatorhalt und wäre schon
   * übersprungen, bevor jemand die Tabulatortaste berührt.
   *
   * Die Bedingung hängt deshalb am **Pfad** und nicht an einem „erstes Mal"-
   * Merker. Der erste Entwurf hatte einen, und er war im Entwicklungsmodus
   * sofort verbraucht: React ruft Effekte unter `StrictMode` zweimal auf
   * (mounten, abräumen, mounten), ein `useRef` überlebt das, und beim zweiten
   * Durchlauf stand er auf „schon dagewesen". Der Fokus sprang also genau
   * dort, wo er nicht springen sollte — und im Produktionsbau nicht, was die
   * Sorte Unterschied ist, die man erst im Feld bemerkt.
   */
  useEffect(() => {
    document.title = seitentitel(ort.pathname);
    const vorher = vorigerPfad.current;
    vorigerPfad.current = ort.pathname;
    // Kein voriger Pfad heißt: Diese Ansicht ist die erste. Derselbe Pfad
    // heißt: Es war kein Wechsel, sondern ein zweiter Aufruf desselben
    // Effekts.
    if (vorher === null || vorher === ort.pathname) return;
    inhalt.current?.focus();
  }, [ort.pathname]);

  // Escape schließt und gibt den Fokus zurück auf den Knopf. Wer mit der
  // Tastatur arbeitet, landet sonst im Nichts: Das Menü ist fort, der Fokus
  // hing an einem Element, das es nicht mehr gibt.
  useEffect(() => {
    if (!menueOffen) return;
    function beiTaste(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setzeMenue(false);
        knopf.current?.focus();
      }
    }
    window.addEventListener("keydown", beiTaste);
    return () => window.removeEventListener("keydown", beiTaste);
  }, [menueOffen]);

  return (
    <div className="shell" data-menue={menueOffen ? "offen" : "zu"}>
      {/* Erster Tabulatorhalt: Wer mit der Tastatur arbeitet, springt sonst
          durch die ganze Navigation, bevor er im Inhalt ankommt - und im
          Rahmen wäre das jedes Mal aufs Neue der Fall. */}
      <a className="shell__sprung" href="#inhalt">
        Zum Inhalt springen
      </a>

      <header className="shell__kopf">
        <button
          ref={knopf}
          type="button"
          className="shell__menueknopf"
          aria-expanded={menueOffen}
          aria-controls={menueId}
          onClick={() => setzeMenue((offen) => !offen)}
        >
          {/* Text statt Symbol: `07` §7.14 verlangt kurze, eindeutige Texte
              und verbietet wichtige Aktionen in versteckten Gesten. Drei
              Striche sind für viele Menschen kein Menü. */}
          Menü
        </button>

        <span className="shell__marke">{MARKE}</span>

        {/* `07` §7.3 verlangt einen globalen „Neu"-Dialog. Er steht im Kopf und
            nicht hinter einer Geste: `07` §7.14 schlieszt wichtige Aktionen in
            versteckten Gesten aus. Kein Eintrag sichtbar, kein Knopf — ein
            Knopf, der einen leeren Dialog oeffnet, ist schlimmer als keiner. */}
        {neueintraege.length > 0 && (
          <button
            type="button"
            className="shell__neu"
            onClick={() => setzeNeu(true)}
          >
            Neu
          </button>
        )}

        <nav className="shell__nav" id={menueId} aria-label="Hauptnavigation">
          <ul>
            {punkte.map((punkt) => (
              <li key={punkt.pfad}>
                <NavLink
                  to={punkt.pfad}
                  end={punkt.pfad === "/"}
                  className={({ isActive }) =>
                    isActive ? "shell__punkt shell__punkt--aktiv" : "shell__punkt"
                  }
                >
                  {punkt.text}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {kopfZusatz}

        <label className="shell__erscheinung">
          Darstellung
          {/* Drei Werte, nicht zwei. „Wie das Gerät" ist die Vorgabe und
              zugleich der Rückweg: Ohne ihn legt ein einziger Griff an diesen
              Umschalter die Shell dauerhaft fest, und die Geräteeinstellung
              eines Baustellentablets bliebe für immer unbeachtet. */}
          <select
            value={wahl}
            onChange={(e) => beiWahl(e.target.value as Wahl)}
          >
            {WAHLEN.map((w) => (
              <option key={w} value={w}>
                {WAHLTEXT[w]}
              </option>
            ))}
          </select>
        </label>
      </header>

      <Dialog offen={neuOffen} beiSchliessen={neuSchliessen} titel="Neu anlegen">
        {neuInhalt}
        <p className="leise">
          {/*
            Der Satz „Anlegen kann er noch nichts" stand hier, bis der Auftrag
            wirklich anlegbar wurde. Er stimmte für alle fünf Einträge und
            stimmt jetzt für vier — und ein Hinweis, der pauschal etwas
            Falsches sagt, ist schlechter als keiner.
          */}
          Die übrigen Einträge führen dorthin, wo die Sache entsteht; angelegt wird
          dort erst mit dem jeweiligen Adapter. <code>07</code> §7.3 sieht den Dialog
          zusätzlich <strong>kontextbezogen</strong> vor: Er soll den offenen Auftrag
          übernehmen — das entsteht mit der Auftragsakte als Kontextquelle.
        </p>
        <ul className="shell__neuliste">
          {neueintraege.map((e) => (
            <li key={e.pfad}>
              <NavLink to={e.pfad} className="shell__neupunkt">
                {e.text}
              </NavLink>{" "}
              <span className="leise">— entsteht in {e.paket}</span>
            </li>
          ))}
        </ul>
      </Dialog>

      <main id="inhalt" className="shell__inhalt" tabIndex={-1} ref={inhalt}>
        {/* Eine begrenzte, mittige Spalte statt der vollen Fensterbreite. Auf
            einem Bürobildschirm liefe der Inhalt sonst am linken Rand entlang
            und ließe rechts eine leere Fläche — die Seite wirkt dann nicht
            großzügig, sondern unfertig. */}
        <div className="shell__spalte">
          {band}
          {/* Die Grenze liegt **innerhalb** der Shell: Was scheitert, ist eine
              Ansicht. Header und Navigation haben damit nichts zu tun und
              bleiben bedienbar — sonst bliebe nach einem Fehler nur das
              Neuladen. Der Pfad setzt sie zurück, sonst stünde die Meldung
              auch in der nächsten Ansicht noch. */}
          <Fehlergrenze zuruecksetzenBei={ort.pathname}>
            <Outlet />
          </Fehlergrenze>
        </div>
      </main>
    </div>
  );
}
